/**
 * Fair Search (/searchList) route SEO — title, description, canonical, robots.
 * SEO copy is English (site default lang). Design / layout unchanged.
 */

import type { LocationQuery, RouteLocationNormalizedLoaded } from 'vue-router'
import {
  FAIR_SEO_DEFAULT_DESCRIPTION,
  FAIR_SEO_DEFAULT_TITLE,
  fairOgImageUrl,
} from '@/seo/fairSeo.defaults'
import type { FairRouteSeo } from '@/seo/routeSeo'

/** Query keys that do not make a search URL “faceted” for index policy. */
const FAIR_SEARCH_SEO_QUERY_ALLOWLIST = new Set([
  'page',
  'image',
  'audience',
])

export const FAIR_SEO_ROBOTS_INDEX = 'index,follow'
export const FAIR_SEO_ROBOTS_NOINDEX = 'noindex,follow'

export interface FairSearchSeoOptions {
  /** When known from shopping-list response; omit until first load. */
  resultCount?: number | null
}

function resolveSearchQuery(
  raw: string | string[] | undefined | null,
): string {
  const value = Array.isArray(raw) ? raw[0] : raw
  if (typeof value !== 'string' || value.length === 0) {
    return ''
  }
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

function getQueryParam(
  query: LocationQuery,
  key: string,
): string | undefined {
  const raw = query[key]
  if (Array.isArray(raw)) {
    return typeof raw[0] === 'string' ? raw[0] : undefined
  }
  return typeof raw === 'string' ? raw : undefined
}

function getSearchPageNumber(query: LocationQuery): number {
  const parsed = Number(getQueryParam(query, 'page'))
  return Number.isNaN(parsed) || parsed <= 0 ? 1 : parsed
}

function hasFairSearchSeoFacets(query: LocationQuery): boolean {
  for (const key of Object.keys(query)) {
    if (FAIR_SEARCH_SEO_QUERY_ALLOWLIST.has(key.toLowerCase())) {
      continue
    }
    return true
  }
  return false
}

export function fairSearchCanonicalPath(searchQuery: string): string {
  if (!searchQuery) {
    return '/searchList'
  }
  return `/searchList/${encodeURIComponent(searchQuery)}`
}

export function buildFairSearchTitle(searchQuery: string): string {
  if (!searchQuery) {
    return FAIR_SEO_DEFAULT_TITLE
  }
  return `${searchQuery} — FAIR`
}

export function buildFairSearchDescription(
  searchQuery: string,
  resultCount?: number | null,
): string {
  if (!searchQuery) {
    return FAIR_SEO_DEFAULT_DESCRIPTION
  }
  if (resultCount != null && resultCount >= 0) {
    return `Browse ${resultCount} results for "${searchQuery}" on FAIR.`
  }
  return `Browse results for "${searchQuery}" on FAIR.`
}

/**
 * Indexable only for a clean query URL:
 * non-empty searchText, no image, no facet keys, page absent or 1.
 */
export function isFairSearchSeoIndexable(
  searchQuery: string,
  query: LocationQuery,
): boolean {
  if (!searchQuery) {
    return false
  }
  if (getQueryParam(query, 'image')) {
    return false
  }
  if (getSearchPageNumber(query) > 1) {
    return false
  }
  if (hasFairSearchSeoFacets(query)) {
    return false
  }
  return true
}

export function buildFairSearchSeo(
  route: Pick<RouteLocationNormalizedLoaded, 'params' | 'query'>,
  options: FairSearchSeoOptions = {},
): FairRouteSeo {
  const searchQuery = resolveSearchQuery(route.params.searchText)
  const path = fairSearchCanonicalPath(searchQuery)
  const indexable = isFairSearchSeoIndexable(searchQuery, route.query)

  return {
    title: buildFairSearchTitle(searchQuery),
    description: buildFairSearchDescription(
      searchQuery,
      options.resultCount,
    ),
    path,
    ogImage: fairOgImageUrl(),
    robots: indexable ? FAIR_SEO_ROBOTS_INDEX : FAIR_SEO_ROBOTS_NOINDEX,
  }
}
